<script lang="ts">
  /// Auth0 ロール 複数選択 (ACC-7 / #418)。招待・ロール変更で共有するチップ + 追加ドロップダウン。
  /// AccountMultiSelect を範にした汎用 UI。将来カスタム / 機能別ロールが増える前提で、
  /// テナントのロールカタログ (id + name) から検索的に複数選択する。
  ///
  /// 候補は `canGrantRole`(actorRole) で出し分ける (backend grant guard の UI ミラー):
  /// org_admin には super_admin / 未知 (CRM 4 値外) ロールを出さない。super_admin は全件。
  /// label は `accountRoleLabel` で CRM ロールを日本語化し、カスタムロールは raw name を出す。
  /// FSD: shared は session を直接読まない (features 依存になる)。呼び出し側 (features) が
  /// `actorRole` を渡す。
  import { accountRoleLabel } from '@shared/types/account';
  import type { AdminRole } from '@shared/types/admin';
  import { canGrantRole, type AppRole } from '@shared/types/session';

  interface Props {
    /// 選択中の Auth0 role_id 配列。
    value: string[];
    /// 選択候補となるロールカタログ (id + name)。親が listOrgRoles / listRoles でロードして渡す。
    roles: AdminRole[];
    /// 操作者のロール (候補の出し分けに使う)。features が session から解決して渡す。
    actorRole: AppRole | null;
    disabled?: boolean;
    onChange?: (value: string[]) => void;
  }

  let {
    value = $bindable([]),
    roles,
    actorRole,
    disabled = false,
    onChange,
  }: Props = $props();

  // 追加ドロップダウンの選択値 (選択直後に '' に戻す)。
  let pending = $state('');

  // 付与できるロールのみ候補に出す (権限昇格ガードの UI ミラー)。CRM 4 値に正規化できない
  // カスタム / 機能別ロールは super_admin にのみ出す (org_admin には backend が grant を拒否する)。
  let grantable = $derived(
    actorRole ? roles.filter((r) => canGrantRole(actorRole, r.name as AppRole)) : [],
  );
  // まだ選択されていない候補のみを追加ドロップダウンに出す。
  let addable = $derived(grantable.filter((r) => !value.includes(r.id)));

  // 選択中 id を AdminRole に解決 (カタログ外でも id を detached 表示で温存)。
  let selected = $derived(
    value.map((id) => ({
      id,
      role: roles.find((r) => r.id === id) ?? null,
    })),
  );

  function label(role: AdminRole): string {
    return accountRoleLabel(role.name);
  }

  function add() {
    if (!pending || value.includes(pending)) {
      pending = '';
      return;
    }
    value = [...value, pending];
    pending = '';
    onChange?.(value);
  }

  function remove(id: string) {
    value = value.filter((v) => v !== id);
    onChange?.(value);
  }
</script>

<div class="multi">
  {#if selected.length > 0}
    <ul class="chips">
      {#each selected as item (item.id)}
        <li class="chip" class:detached={item.role === null}>
          <span class="chip-label">{item.role ? label(item.role) : item.id}</span>
          {#if !disabled}
            <button
              type="button"
              class="chip-remove"
              aria-label="ロールを外す"
              onclick={() => remove(item.id)}
            >×</button>
          {/if}
        </li>
      {/each}
    </ul>
  {/if}

  {#if !disabled}
    <select bind:value={pending} onchange={add} {disabled}>
      <option value="">ロールを追加…</option>
      {#each addable as r (r.id)}
        <option value={r.id}>{label(r)}</option>
      {/each}
    </select>
  {/if}

  {#if selected.length === 0 && disabled}
    <span class="muted">—</span>
  {/if}
</div>

<style>
  .multi {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #eef2ff;
    color: #3730a3;
    padding: 2px 4px 2px 8px;
    border-radius: 999px;
    font-size: 0.75rem;
  }
  .chip.detached {
    background: #f3f4f6;
    color: #6b7280;
  }
  .chip-remove {
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font-size: 0.85rem;
    line-height: 1;
    padding: 0 2px;
  }
  select {
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: #fff;
    align-self: flex-start;
  }
  .muted {
    color: #6b7280;
    font-size: 0.875rem;
  }
</style>
